/**
 * PlayCanvas SSE stream manager.
 *
 * rankRunsForStreaming — pure function for ranking running stations by camera
 * distance and selecting those eligible for live streams. Exported for tests.
 *
 * StreamSystem — manages at most MAX_STREAMS live SSE connections to the
 * stations closest to the camera within STREAM_RADIUS. Checks every 2 s.
 */
import * as pc from 'playcanvas'
import type { StreamHandle } from '../../api/interface'
import { useArenaStore } from '../../store'

export const STREAM_RADIUS = 20
export const MAX_STREAMS = 6
const STREAM_RECONCILE_MS = 2_000

// ── Pure ranking helper ───────────────────────────────────────────────────

/**
 * Returns up to MAX_STREAMS run IDs sorted nearest-first, filtered to those
 * within STREAM_RADIUS of the camera. Pure function — no side effects.
 */
export function rankRunsForStreaming(
  runningIds: string[],
  positions: Map<string, [number, number, number]>,
  camX: number,
  camZ: number,
  maxStreams: number,
  streamRadius: number,
): string[] {
  const ranked = runningIds
    .map((runId) => {
      const pos = positions.get(runId)
      if (!pos) return null
      const dx = camX - pos[0]
      const dz = camZ - pos[2]
      return { runId, dist: Math.sqrt(dx * dx + dz * dz) }
    })
    .filter((x): x is { runId: string; dist: number } => x !== null)
    .sort((a, b) => a.dist - b.dist)

  const result: string[] = []
  for (const { runId, dist } of ranked) {
    if (dist > streamRadius || result.length >= maxStreams) break
    result.push(runId)
  }
  return result
}

// ── PlayCanvas stream system ──────────────────────────────────────────────

export class StreamSystem {
  private cameraEntity!: pc.Entity
  private streams = new Map<string, StreamHandle>()
  private lastEventIds = new Map<string, string>()
  private accumMs = 0
  private unsubscribe?: () => void

  init(cameraEntity: pc.Entity): void {
    this.cameraEntity = cameraEntity
    this.unsubscribe = useArenaStore.subscribe((state, prev) => {
      if (state.apiClient === prev.apiClient) return

      // A stream and its resume cursor belong to the authenticated client that
      // opened it. Cancel both on token/user replacement so no late callback
      // can publish the previous user's transcript into the new session.
      this.cancelAll()
      this.lastEventIds.clear()
      // Reconcile on the next frame rather than waiting a full interval.
      this.accumMs = STREAM_RECONCILE_MS
    })
  }

  /**
   * Called each frame (from app.on('update')). Checks every 2 s whether the
   * set of streams should change based on current camera position.
   */
  frame(dt: number, positions: Map<string, [number, number, number]>): void {
    this.accumMs += dt * 1000
    if (this.accumMs < STREAM_RECONCILE_MS) return
    this.accumMs = 0

    const state = useArenaStore.getState()
    const client = state.apiClient
    if (!client) return

    const camPos = this.cameraEntity.getPosition()
    const runningIds = state.runs
      .filter((r) => r.status === 'running')
      .map((r) => r.id)

    const toStream = new Set(
      rankRunsForStreaming(
        runningIds,
        positions,
        camPos.x,
        camPos.z,
        MAX_STREAMS,
        STREAM_RADIUS,
      ),
    )

    // Cancel out-of-range streams
    for (const [runId, handle] of this.streams) {
      if (!toStream.has(runId)) {
        handle.cancel()
        this.streams.delete(runId)
      }
    }

    // Open streams for newly in-range stations
    for (const runId of toStream) {
      if (this.streams.has(runId)) continue
      const cursor = this.lastEventIds.get(runId)
      let handle!: StreamHandle
      handle = client.streamTranscript(
        runId,
        (event) => {
          if (useArenaStore.getState().apiClient !== client) return
          this.lastEventIds.set(runId, event.id)
          useArenaStore.getState().appendTranscriptEvent(event)
        },
        () => {
          // Stream died — remove so we retry next check (with cursor)
          if (this.streams.get(runId) === handle) this.streams.delete(runId)
        },
        cursor,
        () => {
          if (useArenaStore.getState().apiClient !== client) return
          useArenaStore.getState().replaceTranscriptEvents(runId, [])
          this.lastEventIds.delete(runId)
        },
      )
      this.streams.set(runId, handle)
    }
  }

  destroy(): void {
    this.unsubscribe?.()
    this.unsubscribe = undefined
    this.cancelAll()
    this.lastEventIds.clear()
  }

  private cancelAll(): void {
    for (const handle of this.streams.values()) handle.cancel()
    this.streams.clear()
  }
}
